/**
 * Collapses a stored position/rotation/scale into a numeric `[x, y, z]` tuple,
 * expanding a scalar (legacy uniform-scale shape) to a vec3 and falling back to
 * `fallback` when the value is absent or malformed — a non-finite component
 * (null, a string) rejects the whole value rather than reaching a formatter.
 *
 * @param value - Stored value: absent, a scalar, or a `[x, y, z]` tuple.
 * @param fallback - Value to repeat across all three axes when `value` is unusable.
 */
export const toTuple = (
  value: number | number[] | undefined,
  fallback: number,
): [number, number, number] => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return [value, value, value];
  }
  if (
    Array.isArray(value) &&
    value.length === 3 &&
    value.every((component) => Number.isFinite(component))
  ) {
    return [value[0], value[1], value[2]] as [number, number, number];
  }
  return [fallback, fallback, fallback];
};

/**
 * Formats a stored position/rotation/scale as the three string values that
 * seed the per-axis inputs.
 *
 * @param value - Stored value: absent, a scalar, or a `[x, y, z]` tuple.
 * @param fallback - Value to repeat across all three axes when `value` is unusable.
 * @param decimals - When given, each axis is fixed to this many decimal
 * places for display (Location/Rotation/Scale all use 2); omitted keeps
 * full precision.
 */
export const deriveAxesInputs = (
  value: number | number[] | undefined,
  fallback: number,
  decimals?: number,
): { x: string; y: string; z: string } => {
  const [x, y, z] = toTuple(value, fallback);
  const format = (n: number) =>
    decimals !== undefined ? n.toFixed(decimals) : String(n);
  return { x: format(x), y: format(y), z: format(z) };
};
